
/**
 * Type for operation options
 */
export interface OperationOptions {
  showToast?: boolean;
  successMessage?: string;
  errorMessage?: string;
  retries?: number;
  delay?: number;
  onError?: (error: unknown) => void;
}

/**
 * Safe wrapper for database/API operations with consistent error handling
 */
export const safeOperation = async <T>(
  operation: () => Promise<T>,
  options: OperationOptions = {}
): Promise<T | null> => {
  const {
    errorMessage = 'Operation failed',
    retries = 0,
    delay = 1000,
    onError
  } = options;
  
  let lastError: unknown;
  
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const result = await operation();
      return result;
    } catch (error) {
      console.error(`${errorMessage} (attempt ${attempt + 1}/${retries + 1}):`, error);
      lastError = error;
      
      // Capture in global error handler if available
      if (typeof window !== 'undefined' && window.captureError) {
        window.captureError(error);
      }
      
      // Call custom error handler if provided, using setTimeout to avoid React scheduler errors
      if (onError) {
        setTimeout(() => {
          try {
            onError(error);
          } catch (callbackError) {
            console.error('Error in error handler callback:', callbackError);
          }
        }, 0);
      }
      
      if (attempt < retries) {
        // Wait before retrying
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }
  
  return null;
};

/**
 * Serialize error for better logging and display
 */
export const serializeError = (error: unknown): string => {
  if (error instanceof Error) {
    return `${error.name}: ${error.message}${error.stack ? `\nStack: ${error.stack}` : ''}`;
  }
  
  if (typeof error === 'object' && error !== null) {
    try {
      return JSON.stringify(error);
    } catch (e) {
      return 'Unserializable error object';
    }
  }
  
  return String(error);
};

/**
 * Create a retry mechanism for operations that might fail
 */
export const withRetry = async <T>(
  operation: () => Promise<T>,
  { retries = 3, delay = 1000, onError }: { retries?: number; delay?: number; onError?: (error: unknown) => void } = {}
): Promise<T> => {
  let lastError: unknown;
  
  for (let attempt = 0; attempt < retries; attempt++) {
    try {
      return await operation();
    } catch (error) {
      console.log(`Operation failed (attempt ${attempt + 1}/${retries})`, error);
      lastError = error;
      
      // Capture in global error handler if available
      if (typeof window !== 'undefined' && window.captureError) {
        window.captureError(error);
      }
      
      // Call custom error handler if provided, using setTimeout to avoid React scheduler errors
      if (onError) {
        setTimeout(() => {
          try {
            onError(error);
          } catch (callbackError) {
            console.error('Error in error handler callback:', callbackError);
          }
        }, 0);
      }
      
      if (attempt < retries - 1) {
        // Wait before retrying
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }
  
  throw lastError;
};

/**
 * Standard error handler for API operations
 */
export const handleApiError = (error: unknown, customMessage: string = "An error occurred") => {
  console.error(customMessage, error);
  
  // Capture in global error handler if available
  if (typeof window !== 'undefined' && window.captureError) {
    window.captureError(error);
  }
  
  return null;
};

/**
 * Check for common resource loading errors
 */
export const checkResourceErrors = () => {
  if (typeof window === 'undefined' || typeof performance.getEntriesByType !== 'function') {
    return [];
  }
  
  const resources = performance.getEntriesByType('resource');
  return resources.filter((r) => {
    if (r instanceof PerformanceResourceTiming) {
      // Check for resources that took too long or had errors
      return r.responseStart === 0 && r.duration > 3000; // Resources taking more than 3s
    }
    return false;
  }).map(r => r.name);
};

/**
 * Creates a global error handler for uncaught exceptions
 */
export const setupGlobalErrorHandling = () => {
  if (typeof window !== 'undefined') {
    // Initialize error array if it doesn't exist
    window.APP_ERRORS = window.APP_ERRORS || [];
    
    // Create error capture function
    window.captureError = (error: any) => {
      console.error('Application error captured:', error);
      window.APP_ERRORS.push(error);
    };
    
    // Connect to window error events
    window.addEventListener('error', (event) => {
      window.captureError(event.error || event);
    });
    
    window.addEventListener('unhandledrejection', (event) => {
      window.captureError(event.reason);
    });

    // Apply React scheduler patch immediately
    patchReactScheduler();
  }
};

/**
 * Enhanced scheduler patch that uses a direct polyfill approach
 * rather than modifying React internals, which can cause issues.
 */
export const patchReactScheduler = () => {
  if (typeof window !== 'undefined') {
    try {
      console.log('Applying React scheduler patch...');
      
      // Create a robust global scheduler polyfill if not already exists
      if (!window.ReactSchedulerPolyfill) {
        window.ReactSchedulerPolyfill = {
          scheduleCallback: (callback: () => void) => {
            // Convert setTimeout ID to number using unary plus
            return +setTimeout(() => {
              try {
                callback();
              } catch (e) {
                console.error('Error in scheduler callback:', e);
                if (window.captureError) {
                  window.captureError(e);
                }
              }
            }, 0);
          },
          cancelCallback: (callbackId: number) => {
            clearTimeout(callbackId);
          }
        };
      }
      
      // Create direct references to the real scheduler functions if they exist
      // This helps with compatibility for different React versions
      try {
        const scheduler = (window as any).scheduler || (window as any).__REACT_SCHEDULER__;
        if (scheduler && typeof scheduler.unstable_scheduleCallback === 'function') {
          console.log('Found real React scheduler, using it instead of polyfill');
          window.ReactSchedulerPolyfill.scheduleCallback = scheduler.unstable_scheduleCallback;
          window.ReactSchedulerPolyfill.cancelCallback = scheduler.unstable_cancelCallback;
        }
      } catch (e) {
        console.warn('Could not access React scheduler directly:', e);
      }
      
      // Add safety net for scheduler-related errors
      window.addEventListener('error', (event) => {
        if (event.message && 
          (event.message.includes('unstable_schedule') || 
           event.message.includes('Cannot read properties of undefined') ||
           event.message.includes('is not a function') && 
           (event.message.includes('Ho') || event.message.includes('scheduler')))) {
          
          console.error('React scheduler error intercepted:', event);
          event.preventDefault(); // Prevent the error from crashing the app
          return true;
        }
      }, true); // Use capturing phase to catch errors early
      
      console.log('React scheduler patch applied successfully');
    } catch (error) {
      console.error('Failed to apply scheduler patch:', error);
    }
  }
};

// Add type definition for window extensions
declare global {
  interface Window {
    APP_ERRORS: any[];
    APP_DEBUG?: boolean;
    captureError: (error: any) => void;
    ReactSchedulerPolyfill: {
      scheduleCallback: (callback: () => void) => number;
      cancelCallback: (callbackId: number) => void;
    };
  }
}
